import { createSlice } from "@reduxjs/toolkit";
import { socket } from "../sockets";
import { getDetails, primaryType } from "../decks/cardDetails";
import { doubleEvoOtherSide } from "../decks/doubleEvo";

// Seed a field slot's Atk/Def overlay from a follower's printed stats — the same
// `customValues` the "Modify Atk/Def" menu drives, so they stay adjustable with
// the +/- controls afterwards. Called whenever a follower reaches the top row:
// played from hand, evolved ("<Name> Evolved"), or an Advanced card from the
// evolve deck ("<Name> ADVANCED", typed "Follower / Advanced"). Only acts on
// followers in the top row (0-4); spells/amulets and the EX area (5-9) are left
// untouched. Returns true when it set values, so the caller can broadcast them.
const seedFollowerStats = (state, card, index) => {
  if (index >= 5 || primaryType(card) !== "Follower") return false;
  const d = getDetails(card);
  if (!d) return false;
  state.customValues[index] = {
    showAtk: true,
    atk: d.attack,
    showDef: true,
    def: d.defense,
  };
  return true;
};

// Human name for a board row by slot index: 0-4 is the field (top row), 5-9 is
// the EX area (bottom row). Used by game-log texts so placements into the EX
// area aren't mislabelled as "field".
const rowName = (index) => (index >= 5 ? "EX area" : "field");

const num = (v, fallback) => {
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
};

// Seed a slot's Atk/Def when a follower EVOLVES. Rather than overwriting with the
// evolved card's printed stats, apply the base→evolved stat *delta* on top of the
// follower's currently shown stats, so a buffed/reduced follower keeps that
// difference. E.g. a base-2/4 follower currently at 2/1 evolving into a printed
// 3/5 (delta +1/+1) becomes 3/2. `baseCard` is the card still underneath
// (state.field[index]); `evolvedCard` is the evolved form going onto the field.
// Falls back to the evolved printed stats when the base or current stats are
// unknown. Top row (0-4) followers only; returns true when it set values.
const seedEvolveStats = (state, baseCard, evolvedCard, index) => {
  if (index >= 5 || primaryType(evolvedCard) !== "Follower") return false;
  const evo = getDetails(evolvedCard);
  if (!evo) return false;
  const evoAtk = num(evo.attack, NaN);
  const evoDef = num(evo.defense, NaN);
  const base = getDetails(baseCard);
  const baseAtk = num(base && base.attack, NaN);
  const baseDef = num(base && base.defense, NaN);
  const cur = state.customValues[index] || {};
  let atk = evoAtk;
  let def = evoDef;
  if (![evoAtk, evoDef, baseAtk, baseDef].some(Number.isNaN)) {
    const curAtk = cur.showAtk ? num(cur.atk, baseAtk) : baseAtk;
    const curDef = cur.showDef ? num(cur.def, baseDef) : baseDef;
    atk = curAtk + (evoAtk - baseAtk);
    def = curDef + (evoDef - baseDef);
  }
  state.customValues[index] = { showAtk: true, atk, showDef: true, def };
  return true;
};

export const CardSlice = createSlice({
  name: "card",
  initialState: {
    deck: [],
    evoDeck: [],
    initialDeck: [],
    initialEvoDeck: [],
    // Per-card rarity/art choice (name -> printing cardNo). `myArt` is set from
    // the selected deck's `art` field at game start; `enemyArt` is filled from
    // the opponent's synced `myArt`. The Game still identifies cards by name —
    // these only pick which texture to render, so gameplay/logic are unaffected.
    myArt: {},
    enemyArt: {},
    // Tokens chosen in the deck builder; Token.js shows these first (search
    // still finds every token). Local-only — not synced to the opponent.
    favoriteTokens: [],
    cardback: "",
    enemyCardback: "",
    hand: [],
    handInstanceIds: [],
    cemeteryInstanceIds: [],
    fieldInstanceIds: Array(10).fill(null),
    enemyFieldInstanceIds: Array(10).fill(null),
    enemyHand: [],
    showDice: false,
    showEnemyHand: false,
    showHand: false,
    enemyRevealedCards: [],
    enemyRevealedCardsOpen: false,
    cardSelectedInHand: -1,
    enemyCardSelectedInHand: -1,
    enemyArrow: { idx: -1, show: false },
    leaderActive: false,
    enemyLeaderActive: false,
    superEvoActive: false,
    enemySuperEvoActive: false,
    enemyViewingHand: false,
    enemyLeftGame: false,
    enemyViewingDeck: false,
    enemyViewingTopCards: false,
    viewingOpponentTopCards: false,
    enemyViewingCemetery: false,
    enemyViewingEvoDeck: false,
    enemyViewingCemeteryOpponent: false,
    enemyViewingEvoDeckOpponent: false,
    enemyDeckSize: 0,
    enemyLeader: "",
    enemyDice: { show: false, roll: 1 },
    rematchStatus: false,
    enemyRematchStatus: false,
    matchStartEpoch: 0,
    leader: "",
    // The selected deck's class, stashed on game entry so the game can auto-pick
    // a matching leader. Not synced to the opponent (purely local).
    deckClass: "",
    evoPoints: 0,
    enemyEvoPoints: 0,
    // Manual turn counter (purely local — each player tracks their own, just
    // like play points / health). Auto-increments on "Next Turn"; +/- buttons
    // adjust it by hand. Synced so the opponent can read it (enemyTurn).
    turn: 0,
    enemyTurn: 0,
    playPoints: { available: 0, max: 0 },
    enemyPlayPoints: { available: 0, max: 0 },
    playerHealth: 20,
    enemyHealth: 20,
    field: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    evoField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyEvoField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    // Equipment tokens attached to followers, by slot — separate from evoField
    // so a follower can be evolved AND equipped at the same time.
    equipField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyEquipField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    counterField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyCounterField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    exPlayCostField: [null, null, null, null, null, null, null, null, null, null],
    enemyExPlayCostField: [null, null, null, null, null, null, null, null, null, null],
    auraField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyAuraField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    baneField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyBaneField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    wardField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    enemyWardField: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    // Per-slot keyword status labels (e.g. ["Storm","Ward"]) shown as black
    // boxes on the card. Replaces the old aura/bane/ward glow as the status
    // display. Moves/clears with the card via move/clearStatusAtIndex and syncs
    // to the opponent (their keywordField -> our enemyKeywordField).
    keywordField: [[], [], [], [], [], [], [], [], [], []],
    enemyKeywordField: [[], [], [], [], [], [], [], [], [], []],
    currentCard: "",
    currentCardIndex: -1,
    currentEvo: "",
    room: "",
    enemyOnlineStatus: true,
    selfOnlineStatus: true,
    // Finer-grained connection phase for the wifi badge label:
    // "online" | "reconnecting" | "disconnected". Distinct from the boolean
    // selfOnlineStatus (which only drives whether the badge is shown).
    selfConnectionState: "online",
    // True while a full-state resync is in flight after a detected desync
    // (sequence gap). The socket is still connected during this — it's a
    // separate, transient "Resyncing…" indicator, not a disconnect.
    selfResyncing: false,
    activeUsers: 0,
    gameLog: [],
    chatLog: [],
    lastChatMessage: "",
    // Discord identity exchange: my display name/avatar (empty when not
    // signed in) and the opponent's, exchanged via "identity" messages and
    // the stored-state snapshots so chat and the game log can show real names.
    myName: "",
    myAvatar: "",
    enemyName: "",
    enemyAvatar: "",
    enemyIdentityReceived: false,
    cemetery: [],
    enemyCemetery: [],
    banish: [],
    enemyBanish: [],
    // Shared "played" pile shown above each player's cemetery. Both players can
    // drop cards here and move them back out; the array itself is synced.
    played: [],
    enemyEvoDeck: [
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
      { card: "", status: false },
    ],
    engagedField: [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ],
    enemyEngagedField: [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ],
    customStatus: [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ],
    customValues: [
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
    ],
    enemyCustomValues: [
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
      { showAtk: false, atk: 0, showDef: false, def: 0 },
    ],
  },
  reducers: {
    setActiveUsers: (state, action) => {
      state.activeUsers = action.payload;
    },
    setRoom: (state, action) => {
      state.room = action.payload;
    },
    setDeck: (state, action) => {
      state.deck = action.payload;
      state.initialDeck = action.payload;
    },
    setPlayPoints: (state, action) => {
      state.playPoints = action.payload;
      socket.emit("send msg", {
        type: "playPoints",
        data: state.playPoints,
        room: state.room,
      });
    },
    setCardBack: (state, action) => {
      state.cardback = action.payload;
      socket.emit("send msg", {
        type: "cardback",
        data: state.cardback,
        room: state.room,
      });
    },
    setEnemyCardBack: (state, action) => {
      state.enemyCardback = action.payload;
    },
    setLeader: (state, action) => {
      state.leader = action.payload;
      socket.emit("send msg", {
        type: "leader",
        data: state.leader,
        room: state.room,
      });
    },
    setLeaderSilent: (state, action) => {
      state.leader = action.payload;
    },
    setDeckClass: (state, action) => {
      state.deckClass = action.payload;
    },
    setHealth: (state, action) => {
      state.playerHealth = action.payload;
      socket.emit("send msg", {
        type: "health",
        data: state.playerHealth,
        room: state.room,
      });
    },
    // One aggregated game-log entry for a burst of HP clicks. PlayerUI batches
    // the +/- clicks over a 5s window and dispatches the net difference here,
    // so three quick "−1" clicks read as a single "Lost 3 HP".
    logHealthDiff: (state, action) => {
      const diff = action.payload;
      if (!diff) return;
      const text =
        diff < 0
          ? `-${-diff} HP (${state.playerHealth} HP left)`
          : `+${diff} HP (${state.playerHealth} HP left)`;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${text}`,
        },
      ];
      socket.emit("send msg", {
        type: "logHealthDiff",
        updates: [{ type: "log", data: { text } }],
        room: state.room,
      });
    },
    setEvoPoints: (state, action) => {
      state.evoPoints = action.payload;
      socket.emit("send msg", {
        type: "evoPoints",
        data: state.evoPoints,
        room: state.room,
      });
    },
    setTurn: (state, action) => {
      state.turn = action.payload;
      socket.emit("send msg", {
        type: "turn",
        data: state.turn,
        room: state.room,
      });
    },
    setEnemyTurn: (state, action) => {
      state.enemyTurn = action.payload;
    },
    setRematchStatus: (state, action) => {
      state.rematchStatus = action.payload;
      socket.emit("send msg", {
        type: "rematch",
        data: state.rematchStatus,
        room: state.room,
      });
    },
    setEnemyRematchStatus: (state, action) => {
      state.enemyRematchStatus = action.payload;
    },
    setMatchStart: (state, action) => {
      state.matchStartEpoch = action.payload;
    },
    setShowEnemyHand: (state, action) => {
      state.showEnemyHand = action.payload;
      socket.emit("send msg", {
        type: "viewingHand",
        data: action.payload,
        room: state.room,
      });
    },
    // Toggle revealing my own hand to the opponent. When on, the opponent's
    // board renders my face-up cards in place of the usual cardbacks.
    setShowHand: (state, action) => {
      state.showHand = action.payload;
      socket.emit("send msg", {
        type: "showHand",
        data: action.payload,
        room: state.room,
      });
    },
    setShowDice: (state, action) => {
      state.showDice = action.payload;
    },
    setDice: (state, action) => {
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Rolled a dice and got ${action.payload.roll}`,
        },
      ];
      socket.emit("send msg", {
        type: "dice",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyDice: (state, action) => {
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.enemyDice = action.payload;
      if (state.enemyDice.show) {
        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Player 2): Rolled a dice and got ${action.payload.roll}`,
          },
        ];
      }
    },
    setCardSelectedInHand: (state, action) => {
      state.cardSelectedInHand = action.payload;
      const logIndex = (action.payload - state.enemyHand.length) * -1;
      socket.emit("send msg", {
        type: "cardSelected",
        data: action.payload,
        room: state.room,
      });
      if (action.payload !== -1) {
        const date = new Date().toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
        });

        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Me): Selected card #${logIndex} in opponent's hand`,
          },
        ];
        socket.emit("send msg", {
          type: "log",
          data: {
            text: `Selected card #${logIndex} in your hand`,
          },
          room: state.room,
        });
      }
    },
    setEnemyCardSelectedInHand: (state, action) => {
      state.enemyCardSelectedInHand = action.payload;
    },
    setCardSelectedOnField: (state, action) => {
      state.cardSelectedOnField = action.payload;
      // const logIndex = (action.payload - state.enemyHand.length) * -1;
      socket.emit("send msg", {
        type: "cardSelectedField",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyCardSelectedOnField: (state, action) => {
      state.enemyCardSelectedOnField = action.payload;
    },
    setShowEnemyCard: (state, action) => {
      state.showEnemyCard = action.payload;
    },
    setEnemyCard: (state, action) => {
      state.enemyCard = action.payload;
    },
    setEnemyRevealedCards: (state, action) => {
      state.enemyRevealedCards = action.payload;
    },
    setEnemyRevealedCardsOpen: (state, action) => {
      state.enemyRevealedCardsOpen = action.payload;
    },
    setViewingOpponentTopCards: (state, action) => {
      state.viewingOpponentTopCards = action.payload;
    },
    setViewingDeck: (state, action) => {
      socket.emit("send msg", {
        type: "viewingDeck",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyViewingDeck: (state, action) => {
      state.enemyViewingDeck = action.payload;
    },
    setEnemyViewingHand: (state, action) => {
      state.enemyViewingHand = action.payload;
    },
    setViewingTopCards: (state, action) => {
      socket.emit("send msg", {
        type: "viewingTopCards",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyViewingTopCards: (state, action) => {
      state.enemyViewingTopCards = action.payload;
    },
    setEnemyLeftGame: (state, action) => {
      state.enemyLeftGame = action.payload;
    },
    setViewingCemetery: (state, action) => {
      socket.emit("send msg", {
        type: "viewingCemetery",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyViewingCemetery: (state, action) => {
      state.enemyViewingCemetery = action.payload;
    },
    setViewingEvoDeck: (state, action) => {
      socket.emit("send msg", {
        type: "viewingEvoDeck",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyViewingEvoDeck: (state, action) => {
      state.enemyViewingEvoDeck = action.payload;
    },
    setViewingCemeteryOpponent: (state, action) => {
      socket.emit("send msg", {
        type: "viewingCemeteryOpponent",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyViewingCemeteryOpponent: (state, action) => {
      state.enemyViewingCemeteryOpponent = action.payload;
    },
    setViewingEvoDeckOpponent: (state, action) => {
      socket.emit("send msg", {
        type: "viewingEvoDeckOpponent",
        data: action.payload,
        room: state.room,
      });
    },
    setEnemyViewingEvoDeckOpponent: (state, action) => {
      state.enemyViewingEvoDeckOpponent = action.payload;
    },
    setEvoDeck: (state, action) => {
      state.evoDeck = action.payload;
      state.initialEvoDeck = action.payload;
    },
    setEnemyHand: (state, action) => {
      state.enemyHand = action.payload;
    },
    setEnemyDeckSize: (state, action) => {
      state.enemyDeckSize = action.payload;
    },
    setEnemyEvoPoints: (state, action) => {
      state.enemyEvoPoints = action.payload;
    },
    setEnemyPlayPoints: (state, action) => {
      state.enemyPlayPoints = action.payload;
    },
    setEnemyHealth: (state, action) => {
      state.enemyHealth = action.payload;
    },
    setEnemyLog: (state, action) => {
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      if (action.payload.card) {
        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Player 2): ${action.payload.text}`,
            card: action.payload.card,
          },
        ];
      } else {
        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Player 2): ${action.payload.text}`,
          },
        ];
      }
    },
    // Chat entries are objects { time, mine, text, name, avatar } — name and
    // avatar are the sender's Discord identity (empty when not signed in) and
    // travel with the message so the opponent sees them too.
    setChat: (state, action) => {
      // Discord-style timestamp, time only ("10:47 PM") — chats never span days.
      const date = new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const msg =
        typeof action.payload === "string"
          ? { text: action.payload }
          : action.payload || {};
      const entry = {
        time: date,
        mine: true,
        text: msg.text || "",
        name: msg.name || "",
        avatar: msg.avatar || "",
      };
      state.chatLog = [...state.chatLog, entry];
      socket.emit("send msg", {
        type: "chat",
        data: { text: entry.text, name: entry.name, avatar: entry.avatar },
        room: state.room,
      });
    },
    // Announce my Discord identity to the room (and remember it locally, so
    // it also rides along in stored-state snapshots for late joiners).
    setMyIdentity: (state, action) => {
      const { name = "", avatar = "" } = action.payload || {};
      state.myName = name;
      state.myAvatar = avatar;
      socket.emit("send msg", {
        type: "identity",
        data: { name, avatar },
        room: state.room,
      });
    },
    setEnemyIdentity: (state, action) => {
      const { name = "", avatar = "" } = action.payload || {};
      state.enemyName = name;
      state.enemyAvatar = avatar;
      // First time we hear from the opponent: answer with our own identity in
      // case our original announcement raced their room join. The first-time
      // guard stops the exchange from ping-ponging forever.
      if (!state.enemyIdentityReceived && (state.myName || state.myAvatar)) {
        socket.emit("send msg", {
          type: "identity",
          data: { name: state.myName, avatar: state.myAvatar },
          room: state.room,
        });
      }
      state.enemyIdentityReceived = true;
    },
    setEnemyChat: (state, action) => {
      const date = new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      // Old clients send the bare text string; new ones send an object.
      const msg =
        typeof action.payload === "string"
          ? { text: action.payload }
          : action.payload || {};
      state.chatLog = [
        ...state.chatLog,
        {
          time: date,
          mine: false,
          text: msg.text || "",
          name: msg.name || "",
          avatar: msg.avatar || "",
        },
      ];
    },
    setEnemyOnlineStatus: (state, action) => {
      state.enemyOnlineStatus = action.payload;
      console.log("Enemy online status:", action.payload);
    },
    setSelfOnlineStatus: (state, action) => {
      state.selfOnlineStatus = action.payload;
    },
    setSelfConnectionState: (state, action) => {
      state.selfConnectionState = action.payload;
    },
    setSelfResyncing: (state, action) => {
      state.selfResyncing = action.payload;
    },
    setLastChatMessage: (state, action) => {
      // Accepts the chat payload (object) or a bare string from old clients —
      // the notification bubble only ever shows the text.
      const p = action.payload;
      state.lastChatMessage = typeof p === "string" ? p : p?.text || "";
    },
    setViewingCardsLog: (state, action) => {
      let number = action.payload.number;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Looked at top ${number} cards of deck`,
        },
      ];
      socket.emit("send msg", {
        type: "log",
        data: {
          text: `Looked at top ${number} cards of deck`,
        },
        room: state.room,
      });
    },
    setViewingDeckLog: (state, action) => {
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Viewed deck`,
        },
      ];
      socket.emit("send msg", {
        type: "log",
        data: {
          text: `Viewed deck`,
        },
        room: state.room,
      });
    },
    modifyCounter: (state, action) => {
      let newValue = action.payload.value;
      let index = action.payload.index;
      const newCounters = [
        ...state.counterField.slice(0, index),
        newValue,
        ...state.counterField.slice(index + 1),
      ];
      state.counterField = newCounters;

      socket.emit("send msg", {
        type: "counter",
        data: state.counterField,
        room: state.room,
      });
    },
    addAura: (state, action) => {
      let value = action.payload.value;
      let index = action.payload.index;
      const newField = [
        ...state.auraField.slice(0, index),
        value,
        ...state.auraField.slice(index + 1),
      ];
      state.auraField = newField;

      socket.emit("send msg", {
        type: "aura",
        data: state.auraField,
        room: state.room,
      });
    },
    addBane: (state, action) => {
      let value = action.payload.value;
      let index = action.payload.index;
      const newField = [
        ...state.baneField.slice(0, index),
        value,
        ...state.baneField.slice(index + 1),
      ];
      state.baneField = newField;

      socket.emit("send msg", {
        type: "bane",
        data: state.baneField,
        room: state.room,
      });
    },
    addWard: (state, action) => {
      let value = action.payload.value;
      let index = action.payload.index;
      const newField = [
        ...state.wardField.slice(0, index),
        value,
        ...state.wardField.slice(index + 1),
      ];
      state.wardField = newField;

      socket.emit("send msg", {
        type: "ward",
        data: state.wardField,
        room: state.room,
      });
    },
    drawFromDeck: (state) => {
      if (state.deck.length > 0) {
        const card = state.deck[0];
        state.deck = state.deck.slice(1);
        state.hand = [...state.hand, card];
        const date = new Date().toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
        });

        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Me): (Draw) Added ${card} to Hand`,
            card,
          },
        ];

        socket.emit("send msg", {
          type: "draw",
          updates: [
            { type: "log", data: { text: `Draw 1 card` } },
            { type: "hand", data: state.hand },
            { type: "deckSize", data: state.deck.length },
          ],
          room: state.room,
        });
      }
    },
    drawFourFromDeck: (state) => {
      if (state.deck.length > 3) {
        for (let i = 0; i < 4; i++) {
          const card = state.deck[0];
          state.deck = state.deck.slice(1);
          state.hand = [...state.hand, card];
          const date = new Date().toLocaleTimeString("it-IT", {
            hour: "2-digit",
            minute: "2-digit",
          });

          state.gameLog = [
            ...state.gameLog,
            {
              text: `[${date}] (Me): (Draw) Added ${card} to hand`,
              card,
            },
          ];
        }

        socket.emit("send msg", {
          type: "drawFour",
          updates: [
            { type: "log", data: { text: `Draw 4 cards` } },
            { type: "hand", data: state.hand },
            { type: "deckSize", data: state.deck.length },
          ],
          room: state.room,
        });
      }
    },
    mulliganFour: (state) => {
      if (state.hand.length > 0) {
        for (let i = 0; i < 4; i++) {
          if (state.hand.length > 0) {
            const card = state.hand[0];
            state.hand = state.hand.slice(1);
            state.deck = [...state.deck, card];
            const date = new Date().toLocaleTimeString("it-IT", {
              hour: "2-digit",
              minute: "2-digit",
            });

            state.gameLog = [
              ...state.gameLog,
              {
                text: `[${date}] (Me): (Mulligan) ${card} to Deck`,
                card,
              },
            ];
          }
        }

        socket.emit("send msg", {
          type: "mulligan",
          updates: [
            { type: "log", data: { text: `Mulligan 4 cards` } },
            { type: "hand", data: state.hand },
            { type: "deckSize", data: state.deck.length },
          ],
          room: state.room,
        });
      }
    },
    toggleKeyword: (state, action) => {
      const { index, keyword } = action.payload;
      const cur = state.keywordField[index] || [];
      const next = cur.includes(keyword)
        ? cur.filter((k) => k !== keyword)
        : [...cur, keyword];
      state.keywordField = [
        ...state.keywordField.slice(0, index),
        next,
        ...state.keywordField.slice(index + 1),
      ];
      socket.emit("send msg", {
        type: "keyword",
        data: state.keywordField,
        room: state.room,
      });
    },
    setEnemyKeyword: (state, action) => {
      state.enemyKeywordField = action.payload;
    },
    clearStatusAtIndex: (state, action) => {
      let index = action.payload;
      const newCustomStatus = [
        ...state.customStatus.slice(0, index),
        false,
        ...state.customStatus.slice(index + 1),
      ];
      state.customStatus = newCustomStatus;
      const newKeywordField = [
        ...state.keywordField.slice(0, index),
        [],
        ...state.keywordField.slice(index + 1),
      ];
      state.keywordField = newKeywordField;
      const newAuraField = [
        ...state.auraField.slice(0, index),
        0,
        ...state.auraField.slice(index + 1),
      ];
      state.auraField = newAuraField;
      const newBaneField = [
        ...state.baneField.slice(0, index),
        0,
        ...state.baneField.slice(index + 1),
      ];
      state.baneField = newBaneField;
      const newWardField = [
        ...state.wardField.slice(0, index),
        0,
        ...state.wardField.slice(index + 1),
      ];
      state.wardField = newWardField;

      socket.emit("send msg", {
        type: "clearStatus",
        updates: [
          { type: "keyword", data: state.keywordField },
          { type: "aura", data: state.auraField },
          { type: "bane", data: state.baneField },
          { type: "ward", data: state.wardField },
        ],
        room: state.room,
      });
    },
    setEngaged: (state, action) => {
      let index = action.payload;
      const newEngaged = [
        ...state.engagedField.slice(0, index),
        !state.engagedField[index],
        ...state.engagedField.slice(index + 1),
      ];
      state.engagedField = newEngaged;
      socket.emit("send msg", {
        type: "engaged",
        data: state.engagedField,
        room: state.room,
      });
    },
    moveStatusAtIndex: (state, action) => {
      const prevIndex = action.payload.prevIndex;
      const index = action.payload.index;
      const prevStatus = state.customStatus[prevIndex];
      const prevAura = state.auraField[prevIndex];
      const prevBane = state.baneField[prevIndex];
      const prevWard = state.wardField[prevIndex];
      const prevKeywords = state.keywordField[prevIndex];

      const newCustomStatus = [
        ...state.customStatus.slice(0, index),
        prevStatus,
        ...state.customStatus.slice(index + 1),
      ];
      state.customStatus = newCustomStatus;

      const newKeywordField = [
        ...state.keywordField.slice(0, index),
        prevKeywords,
        ...state.keywordField.slice(index + 1),
      ];
      state.keywordField = newKeywordField;

      const newAuraField = [
        ...state.auraField.slice(0, index),
        prevAura,
        ...state.auraField.slice(index + 1),
      ];
      state.auraField = newAuraField;
      const newBaneField = [
        ...state.baneField.slice(0, index),
        prevBane,
        ...state.baneField.slice(index + 1),
      ];
      state.baneField = newBaneField;
      const newWardField = [
        ...state.wardField.slice(0, index),
        prevWard,
        ...state.wardField.slice(index + 1),
      ];
      state.wardField = newWardField;
    },
    clearEngagedAtIndex: (state, action) => {
      let index = action.payload;
      const newEngaged = [
        ...state.engagedField.slice(0, index),
        false,
        ...state.engagedField.slice(index + 1),
      ];
      state.engagedField = newEngaged;
      socket.emit("send msg", {
        type: "engaged",
        data: state.engagedField,
        room: state.room,
      });
    },
    moveEngagedAtIndex: (state, action) => {
      const prevIndex = action.payload.prevIndex;
      const index = action.payload.index;
      const prevItem = state.engagedField[prevIndex];
      const newEngaged = [
        ...state.engagedField.slice(0, index),
        prevItem,
        ...state.engagedField.slice(index + 1),
      ];
      state.engagedField = newEngaged;
      socket.emit("send msg", {
        type: "engaged",
        data: state.engagedField,
        room: state.room,
      });
    },
    clearCountersAtIndex: (state, action) => {
      let index = action.payload;
      const newCounters = [
        ...state.counterField.slice(0, index),
        0,
        ...state.counterField.slice(index + 1),
      ];
      state.counterField = newCounters;
      socket.emit("send msg", {
        type: "counter",
        data: state.counterField,
        room: state.room,
      });
    },
    moveCountersAtIndex: (state, action) => {
      const prevIndex = action.payload.prevIndex;
      const index = action.payload.index;
      const prevItem = state.counterField[prevIndex];
      const newCounters = [
        ...state.counterField.slice(0, index),
        prevItem,
        ...state.counterField.slice(index + 1),
      ];
      state.counterField = newCounters;
      socket.emit("send msg", {
        type: "counter",
        data: state.counterField,
        room: state.room,
      });
    },
    showStatus: (state, action) => {
      let index = action.payload;
      const newCustomStatus = [
        ...state.customStatus.slice(0, index),
        true,
        ...state.customStatus.slice(index + 1),
      ];
      state.customStatus = newCustomStatus;
    },
    hideStatus: (state, action) => {
      let index = action.payload;
      const newCustomStatus = [
        ...state.customStatus.slice(0, index),
        false,
        ...state.customStatus.slice(index + 1),
      ];
      state.customStatus = newCustomStatus;
    },
    showAtk: (state, action) => {
      let index = action.payload;
      let item = state.customValues[index];
      item.showAtk = true;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
    },
    hideAtk: (state, action) => {
      let index = action.payload;
      let item = state.customValues[index];
      item.showAtk = false;
      item.atk = 0;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
      socket.emit("send msg", {
        type: "values",
        data: state.customValues,
        room: state.room,
      });
    },
    modifyAtk: (state, action) => {
      let newValue = action.payload.value;
      let index = action.payload.index;
      let item = state.customValues[index];
      item.atk = newValue;
      item.showAtk = true;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
      socket.emit("send msg", {
        type: "values",
        data: state.customValues,
        room: state.room,
      });
    },
    showDef: (state, action) => {
      let index = action.payload;
      let item = state.customValues[index];
      item.showDef = true;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
    },
    hideDef: (state, action) => {
      let index = action.payload;
      let item = state.customValues[index];
      item.showDef = false;
      item.def = 0;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
      socket.emit("send msg", {
        type: "values",
        data: state.customValues,
        room: state.room,
      });
    },
    modifyDef: (state, action) => {
      const newValue = action.payload.value;
      const index = action.payload.index;
      let item = state.customValues[index];
      item.def = newValue;
      item.showDef = true;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
      socket.emit("send msg", {
        type: "values",
        data: state.customValues,
        room: state.room,
      });
    },
    clearValuesAtIndex: (state, action) => {
      const index = action.payload;
      let item = state.customValues[index];
      item.atk = 0;
      item.def = 0;
      item.showAtk = false;
      item.showDef = false;
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        item,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
      socket.emit("send msg", {
        type: "values",
        data: state.customValues,
        room: state.room,
      });
    },
    moveValuesAtIndex: (state, action) => {
      const prevIndex = action.payload.prevIndex;
      const index = action.payload.index;
      // Promoting a card from the EX area (5-9) up to the field (0-4): the EX
      // slot carried no stats, so seed the follower's printed Atk/Def exactly
      // like a card entering the field normally. The field/evoField arrays were
      // already updated by the move reducer dispatched before this one, so the
      // card now at `index` is the one being promoted (evo form on top).
      if (prevIndex >= 5 && index < 5) {
        const movedCard = state.evoField[index] || state.field[index];
        if (seedFollowerStats(state, movedCard, index)) {
          socket.emit("send msg", {
            type: "values",
            data: state.customValues,
            room: state.room,
          });
          return;
        }
      }
      // Moving a follower into the EX area (bottom row, indices 5-9) hides its
      // Atk/Def overlay — the EX area never shows stats (see seedFollowerStats).
      const prevItem =
        index >= 5
          ? { atk: 0, def: 0, showAtk: false, showDef: false }
          : state.customValues[prevIndex];
      const newCustomValues = [
        ...state.customValues.slice(0, index),
        prevItem,
        ...state.customValues.slice(index + 1),
      ];
      state.customValues = newCustomValues;
      socket.emit("send msg", {
        type: "values",
        data: state.customValues,
        room: state.room,
      });
    },
    duplicateCardOnField: (state, action) => {
      const card = action.payload.card;
      const newIndex = action.payload.index;

      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Followers duplicated to the top row start with their Atk/Def icons shown
      // and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to ${rowName(newIndex)}`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "duplicateCardOnField",
        updates: [
          {
            type: "log",
            data: { text: `Added ${card} to ${rowName(newIndex)}`, card },
          },
          { type: "field", data: state.field },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    setCurrentCard: (state, action) => {
      state.currentCard = action.payload;
    },
    setCurrentCardIndex: (state, action) => {
      state.currentCardIndex = action.payload;
    },
    setCurrentEvo: (state, action) => {
      state.currentCard = action.payload;
    },
    shuffleDeck: (state) => {
      function shuffleDeck(deck) {
        for (let i = deck.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [deck[i], deck[j]] = [deck[j], deck[i]];
        }
        return deck;
      }
      state.deck = shuffleDeck(state.deck);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Shuffled deck`,
        },
      ];
      socket.emit("send msg", {
        type: "log",
        data: { text: `Shuffled deck` },
        room: state.room,
      });
    },
    placeToTopOfDeckFromHand: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.hand = state.hand.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.deck = [card, ...state.deck];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Top of Deck from Hand`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeToTopOfDeckFromHand",
        updates: [
          {
            type: "log",
            data: { text: `Added 1 card to Top of Deck from Hand` },
          },
          { type: "deckSize", data: state.deck.length },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    placeToBotOfDeckFromHand: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.hand = state.hand.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.deck = [...state.deck, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Bot of Deck from Hand`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToBotOfDeckFromHand",
        updates: [
          {
            type: "log",
            data: { text: `Added 1 card to Bot of Deck from Hand` },
          },
          { type: "deckSize", data: state.deck.length },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    placeToTopOfDeckFromCemetery: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.cemetery = state.cemetery.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.deck = [card, ...state.deck];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Top of Deck from Cemetery`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeToTopOfDeckFromCemetery",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Top of Deck from Cemetery`, card },
          },
          { type: "deckSize", data: state.deck.length },
          { type: "cemetery", data: state.cemetery },
        ],
        room: state.room,
      });
    },
    placeToBotOfDeckFromCemetery: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.cemetery = state.cemetery.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.deck = [...state.deck, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Bot of Deck from Cemetery`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeToBotOfDeckFromCemetery",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Bot of Deck from Cemetery`, card },
          },
          { type: "cemetery", data: state.cemetery },
        ],
        room: state.room,
      });
    },
    placeToTopOfDeckFromField: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, cardIndex),
        0,
        ...state.field.slice(cardIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.deck = [card, ...state.deck];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Top of Deck from ${rowName(cardIndex)}`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToTopOfDeckFromField",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to Top of Deck from ${rowName(cardIndex)}`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    placeToBotOfDeckFromField: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, cardIndex),
        0,
        ...state.field.slice(cardIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.deck = [...state.deck, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Bot of Deck from ${rowName(cardIndex)}`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToBotOfDeckFromField",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to Bot of Deck from ${rowName(cardIndex)}`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    placeTokenOnField: (state, action) => {
      const card = action.payload.card;
      const newIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Follower tokens played to the top row start with their Atk/Def icons
      // shown and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${rowName(newIndex)}`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeTokenOnField",
        updates: [
          {
            type: "log",
            data: { text: `${card} to ${rowName(newIndex)}`, card },
          },
          { type: "field", data: state.field },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    removeTokenOnField: (state, action) => {
      const card = action.payload.card;
      const prevIndex = action.payload.index;
      const field = [
        ...state.field.slice(0, prevIndex),
        0,
        ...state.field.slice(prevIndex + 1),
      ];
      state.field = field;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Cemetery from ${rowName(prevIndex)}`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "removeTokenOnField",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Cemetery from ${rowName(prevIndex)}`, card },
          },
          { type: "field", data: state.field },
        ],
        room: state.room,
      });
    },
    moveCardOnField: (state, action) => {
      const card = action.payload.card;
      const prevIndex = action.payload.prevIndex;
      const newIndex = action.payload.index;
      const field = [
        ...state.field.slice(0, prevIndex),
        0,
        ...state.field.slice(prevIndex + 1),
      ];
      state.field = field;
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Repositioning within the same row (field→field or EX→EX) is just
      // tidying — don't log it. Only crossing between the EX area and the
      // field is worth an entry, and the text says which way the card went.
      const shouldLog = (prevIndex >= 5) !== (newIndex >= 5);
      const moveText =
        prevIndex >= 5
          ? `Moved ${card} to Field from EX Area`
          : `Moved ${card} to EX Area from Field`;
      if (shouldLog) {
        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Me): ${moveText}`,
            card,
          },
        ];
      }

      socket.emit("send msg", {
        type: "moveCard",
        updates: [
          ...(shouldLog ? [{ type: "log", data: { text: moveText, card } }] : []),
          { type: "field", data: state.field },
        ],
        room: state.room,
      });
    },
    moveEvoAndBaseOnField: (state, action) => {
      // console.log(action.payload.card);
      // console.log(action.payload.evoCard);
      // base
      const card = state.field[action.payload.prevIndex];
      const prevIndex = action.payload.prevIndex;
      const newIndex = action.payload.index;
      const field = [
        ...state.field.slice(0, prevIndex),
        0,
        ...state.field.slice(prevIndex + 1),
      ];
      state.field = field;
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;
      // evo
      const evoCard = action.payload.evoCard;
      const evoField = [
        ...state.evoField.slice(0, prevIndex),
        0,
        ...state.evoField.slice(prevIndex + 1),
      ];
      state.evoField = evoField;
      const newEvoField = [
        ...state.evoField.slice(0, newIndex),
        evoCard,
        ...state.evoField.slice(newIndex + 1),
      ];
      state.evoField = newEvoField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Same rule as moveCardOnField: only moves crossing between the EX area
      // and the field get logged, with the direction spelled out.
      const shouldLog = (prevIndex >= 5) !== (newIndex >= 5);
      const moveText =
        prevIndex >= 5
          ? `Moved ${evoCard} to Field from EX Area`
          : `Moved ${evoCard} to EX Area from Field`;
      if (shouldLog) {
        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Me): ${moveText}`,
            card: evoCard,
          },
        ];
      }
      socket.emit("send msg", {
        type: "moveEvoAndBaseOnField",
        updates: [
          ...(shouldLog
            ? [{ type: "log", data: { text: moveText, card: evoCard } }]
            : []),
          { type: "field", data: state.field },
          { type: "evoField", data: state.evoField },
        ],
        room: state.room,
      });
    },
    transferToOpponentField: (state, action) => {
      if (
        state.enemyField[0] === 0 ||
        state.enemyField[1] === 0 ||
        state.enemyField[2] === 0 ||
        state.enemyField[3] === 0 ||
        state.enemyField[4] === 0
      ) {
        const card = action.payload.card;
        const prevIndex = action.payload.prevIndex;
        const field = [
          ...state.field.slice(0, prevIndex),
          0,
          ...state.field.slice(prevIndex + 1),
        ];
        state.field = field;
        const date = new Date().toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
        });

        let index;
        for (let i = 0; i < 5; i++) {
          if (state.enemyField[i] === 0) {
            index = i;
            break;
          }
        }
        const newField = [
          ...state.enemyField.slice(0, index),
          card,
          ...state.enemyField.slice(index + 1),
        ];
        state.enemyField = newField;

        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Me): Transfered ${card} to enemy field`,
            card,
          },
        ];
        socket.emit("send msg", {
          type: "transferToOpponentField",
          updates: [
            {
              type: "log",
              data: { text: `Transfered ${card} to your field`, card },
            },
            { type: "field", data: state.field },
            { type: "transfer", data: state.enemyField },
          ],
          room: state.room,
        });
      } else {
        console.log("there are no open slots to transfer");
      }
    },

    addToFieldFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.cardIndex;
      const newIndex = action.payload.index;
      state.deck = state.deck.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      // Followers played to the top row start with their Atk/Def icons shown
      // and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${rowName(newIndex)} from Deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToFieldFromDeck",
        updates: [
          {
            type: "log",
            data: { text: `Transfered ${card} to your field`, card },
          },
          { type: "field", data: state.field },
          { type: "deckSize", data: state.deck.length },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    addToTopOfDeckFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      state.deck = state.deck.filter((_, i) => i !== cardIndex);
      state.deck = [card, ...state.deck];
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Moved ${card} to top of deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToTopOfDeckFromDeck",
        updates: [
          {
            type: "log",
            data: { text: `Moved 1 card to top of deck` },
          },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToBotOfDeckFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      state.deck = state.deck.filter((_, i) => i !== cardIndex);
      state.deck = [...state.deck, card];
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Moved ${card} to bot of deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToBotOfDeckFromDeck",
        updates: [
          {
            type: "log",
            data: { text: `Moved 1 card to bot of deck` },
          },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToCemeteryFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      state.deck = state.deck.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.cemetery = [card, ...state.cemetery];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Cemetery from Deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToCemeteryFromDeck",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Cemetery from Deck`, card },
          },
          { type: "cemetery", data: state.cemetery },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToCemeteryFromTopOfDeck: (state) => {
      const card = state.deck[0];
      state.deck = state.deck.slice(1);
      state.cemetery = [card, ...state.cemetery];
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Milled ${card} to cemetery`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToCemeteryFromTopOfDeck",
        updates: [
          {
            type: "log",
            data: { text: `Milled ${card} to cemetery`, card },
          },
          { type: "cemetery", data: state.cemetery },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    // Place the top card of the deck into the first open EX-area slot.
    // The EX area is field indices 5-9 (an empty slot is 0). No-op if the
    // deck is empty or every EX slot is already occupied.
    placeToExFromTopOfDeck: (state) => {
      if (state.deck.length === 0) return;
      const exOffset = 5;
      const relativeIndex = state.field
        .slice(exOffset)
        .findIndex((slot) => slot === 0);
      if (relativeIndex === -1) return;
      const newIndex = exOffset + relativeIndex;

      const card = state.deck[0];
      state.deck = state.deck.slice(1);
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Placed ${card} to EX area from deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToExFromTopOfDeck",
        updates: [
          {
            type: "log",
            data: { text: `Placed ${card} to EX area from deck`, card },
          },
          { type: "field", data: state.field },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToBanishFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      state.deck = state.deck.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.banish = [card, ...state.banish];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Banished ${card} from deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToBanishFromDeck",
        updates: [
          {
            type: "log",
            data: { text: `Banished ${card} from deck`, card },
          },
          { type: "banish", data: state.banish },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToBanishFromTopOfDeck: (state) => {
      const card = state.deck[0];
      state.deck = state.deck.slice(1);
      state.banish = [card, ...state.banish];
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Banished ${card} from top of deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToBanishFromTopOfDeck",
        updates: [
          {
            type: "log",
            data: { text: `Banished ${card} from top of deck`, card },
          },
          { type: "banish", data: state.banish },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToHandFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newDeck = state.deck.filter((_, i) => i !== cardIndex);
      state.deck = newDeck;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.hand = [...state.hand, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to Hand from Deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToHandFromDeck",
        updates: [
          {
            type: "log",
            data: { text: `Added ${card} to Hand from Deck`, card },
          },
          { type: "hand", data: state.hand },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToHandFromDeckWithoutRevealing: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newDeck = state.deck.filter((_, i) => i !== cardIndex);
      state.deck = newDeck;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.hand = [...state.hand, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to Hand from Deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToHandFromDeckWithoutRevealing",
        updates: [
          {
            type: "log",
            data: { text: `Added 1 card to Hand from Deck`, card: "Card" },
          },
          { type: "hand", data: state.hand },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    addToHandFromField: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, cardIndex),
        0,
        ...state.field.slice(cardIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.hand = [...state.hand, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to Hand from ${rowName(cardIndex)}`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToHandFromField",
        updates: [
          {
            type: "log",
            data: {
              text: `Added ${card} to Hand from ${rowName(cardIndex)}`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    placeToCemeteryFromHand: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.hand = state.hand.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Discarded ${card} from Hand`,
          card,
        },
      ];
      state.cemetery = [card, ...state.cemetery];

      socket.emit("send msg", {
        type: "discardFromHand",
        updates: [
          {
            type: "log",
            data: {
              text: `Discarded ${card} from Hand`,
              card,
            },
          },
          { type: "cemetery", data: state.cemetery },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    placeToBanishFromHand: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.hand = state.hand.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Banished ${card} from Hand`,
          card,
        },
      ];
      state.banish = [card, ...state.banish];

      socket.emit("send msg", {
        type: "banishFromHand",
        updates: [
          {
            type: "log",
            data: {
              text: `Banished ${card} from Hand`,
              card,
            },
          },
          { type: "banish", data: state.banish },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    placeToFieldFromHand: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.indexInHand;
      const newIndex = action.payload.index;
      state.hand = state.hand.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      // Followers played to the top row start with their Atk/Def icons shown
      // and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${newIndex >= 5 ? "EX area" : "Field"} from Hand`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeFromHand",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to ${newIndex >= 5 ? "EX area" : "Field"} from Hand`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "hand", data: state.hand },
          // Sync the auto-shown stats so the opponent sees them too (mirrors
          // how modifyAtk/modifyDef broadcast the "values" update).
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    addToHandFromCemetery: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.cemetery = state.cemetery.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.hand = [...state.hand, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Hand from Cemetery`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToHandFromCemetery",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Hand from Cemetery`, card },
          },
          { type: "cemetery", data: state.cemetery },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    addToBanishFromCemetery: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.cemetery = state.cemetery.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.banish = [card, ...state.banish];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Banished ${card} from Cemetery`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToBanishFromCemetery",
        updates: [
          {
            type: "log",
            data: { text: `Banished ${card} from Cemetery`, card },
          },
          { type: "cemetery", data: state.cemetery },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    addToHandFromBanish: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.banish = state.banish.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.hand = [...state.hand, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to hand from Banished`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToHandFromBanish",
        updates: [
          {
            type: "log",
            data: { text: `Added ${card} to hand from Banished`, card },
          },
          { type: "hand", data: state.hand },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    addToCemeteryFromBanish: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.banish = state.banish.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.cemetery = [card, ...state.cemetery];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to Cemetery from Banished`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "addToCemeteryFromBanish",
        updates: [
          {
            type: "log",
            data: { text: `Added ${card} to Cemetery from Banished`, card },
          },
          { type: "cemetery", data: state.cemetery },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    placeToTopOfDeckFromBanish: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.banish = state.banish.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.deck = [card, ...state.deck];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Top of Deck from Banished`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeToTopOfDeckFromBanish",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Top of Deck from Banished`, card },
          },
          { type: "deckSize", data: state.deck.length },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    placeToBotOfDeckFromBanish: (state, action) => {
      const card = action.payload.name;
      const cardIndex = action.payload.index;
      state.banish = state.banish.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.deck = [...state.deck, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Bot of Deck from Banished`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeToBotOfDeckFromBanish",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Bot of Deck from Banished`, card },
          },
          { type: "deckSize", data: state.deck.length },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    placeToCemeteryFromField: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, cardIndex),
        0,
        ...state.field.slice(cardIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.cemetery = [card, ...state.cemetery];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Cemetery from ${rowName(cardIndex)}`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "placeToCemeteryFromField",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to Cemetery from ${rowName(cardIndex)}`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "cemetery", data: state.cemetery },
        ],
        room: state.room,
      });
    },
    shuffleCards: (state, action) => {
      function shuffleDeck(deck) {
        for (let i = deck.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [deck[i], deck[j]] = [deck[j], deck[i]];
        }
        return deck;
      }
      state.hand = shuffleDeck(state.hand);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Shuffled cards in Hand`,
        },
      ];
      socket.emit("send msg", {
        type: "shuffleCards",
        updates: [
          {
            type: "log",
            data: { text: `Shuffled cards in Hand` },
          },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    placeToFieldFromDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.deckIndex;
      const newIndex = action.payload.index;
      state.deck = state.deck.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      // Followers played to the top row start with their Atk/Def icons shown
      // and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${rowName(newIndex)} from Deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToFieldFromDeck",
        updates: [
          {
            type: "log",
            data: { 
              text: `${card} to ${rowName(newIndex)} from Deck`, 
              card 
            },
          },
          { type: "field", data: state.field },
          { type: "deckSize", data: state.deck.length },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },

    placeToFieldFromCemetery: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.indexInHand;
      const newIndex = action.payload.index;
      state.cemetery = state.cemetery.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      // Followers played to the top row start with their Atk/Def icons shown
      // and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${rowName(newIndex)} from Cemetery`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToFieldFromCemetery",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to ${rowName(newIndex)} from Cemetery`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "cemetery", data: state.cemetery },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    placeToFieldFromBanish: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.indexInHand;
      const newIndex = action.payload.index;
      state.banish = state.banish.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      // Followers played to the top row start with their Atk/Def icons shown
      // and pre-filled to the card's base stats (see seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${rowName(newIndex)} from Banished`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToFieldFromBanish",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to ${rowName(newIndex)} from Banished`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "banish", data: state.banish },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    placeToBanishFromField: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, cardIndex),
        0,
        ...state.field.slice(cardIndex + 1),
      ];
      state.field = newField;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.banish = [card, ...state.banish];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Banished ${card} from ${rowName(cardIndex)}`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "placeToBanishFromField",
        updates: [
          {
            type: "log",
            data: { text: `Banished ${card} from ${rowName(cardIndex)}`, card },
          },
          { type: "field", data: state.field },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    setPlayed: (state, action) => {
      state.played = Array.isArray(action.payload)
        ? action.payload.slice(0, 1)
        : [];
    },
    playCardToSharedZone: (state, action) => {
      const { card, index, from } = action.payload;
      if (!card) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      if (from === "hand") {
        state.hand = state.hand.filter((_, i) => i !== index);
      } else if (from === "field") {
        state.field = [
          ...state.field.slice(0, index),
          0,
          ...state.field.slice(index + 1),
        ];
      } else if (from === "cemetery") {
        state.cemetery = state.cemetery.filter((_, i) => i !== index);
      } else if (from === "banish") {
        state.banish = state.banish.filter((_, i) => i !== index);
      } else if (from === "deck") {
        state.deck = state.deck.filter((_, i) => i !== index);
      }

      // Single-slot zone: if a card is already there, it goes to the cemetery
      // so the new card can take its place.
      const previous = state.played[0];
      if (previous) state.cemetery = [previous, ...state.cemetery];
      state.played = [card];

      if (previous) {
        state.gameLog = [
          ...state.gameLog,
          {
            text: `[${date}] (Me): ${previous} to Cemetery from Played`,
            card: previous,
          },
        ];
      }
      state.gameLog = [
        ...state.gameLog,
        { text: `[${date}] (Me): Played ${card}`, card },
      ];

      const updates = [
        { type: "log", data: { text: `Played ${card}`, card } },
        { type: "played", data: state.played },
      ];
      if (previous) {
        updates.push({
          type: "log",
          data: {
            text: `${previous} to Cemetery from Played`,
            card: previous,
          },
        });
        updates.push({ type: "cemetery", data: state.cemetery });
      }
      if (from === "hand") updates.push({ type: "hand", data: state.hand });
      else if (from === "field")
        updates.push({ type: "field", data: state.field });
      else if (from === "cemetery" && !previous)
        updates.push({ type: "cemetery", data: state.cemetery });
      else if (from === "banish")
        updates.push({ type: "banish", data: state.banish });
      else if (from === "deck")
        updates.push({ type: "deckSize", data: state.deck.length });

      socket.emit("send msg", {
        type: "playCardToSharedZone",
        updates,
        room: state.room,
      });
    },
    movePlayedToHand: (state, action) => {
      const card = state.played[action.payload.index];
      if (!card) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.played = state.played.filter((_, i) => i !== action.payload.index);
      state.hand = [...state.hand, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Hand from Played`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "movePlayedToHand",
        updates: [
          {
            type: "log",
            data: { text: `${card} to Hand from Played`, card },
          },
          { type: "played", data: state.played },
          { type: "hand", data: state.hand },
        ],
        room: state.room,
      });
    },
    movePlayedToField: (state, action) => {
      const card = state.played[action.payload.indexInPlayed];
      const newIndex = action.payload.index;
      if (!card || newIndex === undefined) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.played = state.played.filter(
        (_, i) => i !== action.payload.indexInPlayed,
      );
      state.field = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to ${rowName(newIndex)} from Played`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "movePlayedToField",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to ${rowName(newIndex)} from Played`,
              card,
            },
          },
          { type: "played", data: state.played },
          { type: "field", data: state.field },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    movePlayedToTopOfDeck: (state, action) => {
      const card = state.played[action.payload.index];
      if (!card) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.played = state.played.filter((_, i) => i !== action.payload.index);
      state.deck = [card, ...state.deck];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Top of Deck from Played`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "movePlayedToTopOfDeck",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to Top of Deck from Played`,
              card,
            },
          },
          { type: "played", data: state.played },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    movePlayedToBotOfDeck: (state, action) => {
      const card = state.played[action.payload.index];
      if (!card) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.played = state.played.filter((_, i) => i !== action.payload.index);
      state.deck = [...state.deck, card];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Bot of Deck from Played`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "movePlayedToBotOfDeck",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to Bot of Deck from Played`,
              card,
            },
          },
          { type: "played", data: state.played },
          { type: "deckSize", data: state.deck.length },
        ],
        room: state.room,
      });
    },
    movePlayedToCemetery: (state, action) => {
      const card = state.played[action.payload.index];
      if (!card) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.played = state.played.filter((_, i) => i !== action.payload.index);
      state.cemetery = [card, ...state.cemetery];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): ${card} to Cemetery from Played`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "movePlayedToCemetery",
        updates: [
          {
            type: "log",
            data: {
              text: `${card} to Cemetery from Played`,
              card,
            },
          },
          { type: "played", data: state.played },
          { type: "cemetery", data: state.cemetery },
        ],
        room: state.room,
      });
    },
    movePlayedToBanish: (state, action) => {
      const card = state.played[action.payload.index];
      if (!card) return;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.played = state.played.filter((_, i) => i !== action.payload.index);
      state.banish = [card, ...state.banish];

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Banished ${card} from Played`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "movePlayedToBanish",
        updates: [
          {
            type: "log",
            data: { text: `Banished ${card} from Played`, card },
          },
          { type: "played", data: state.played },
          { type: "banish", data: state.banish },
        ],
        room: state.room,
      });
    },
    evolveCardOnField: (state, action) => {
      const card = action.payload.card;
      const newIndex = action.payload.index;
      const cardIndex = action.payload.indexInEvolveDeck;
      state.evoDeck = state.evoDeck.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const newField = [
        ...state.evoField.slice(0, newIndex),
        card,
        ...state.evoField.slice(newIndex + 1),
      ];
      state.evoField = newField;

      // An evolved follower shows its Atk/Def icons, carrying over any current
      // buff/reduction by applying the base→evolved delta (see seedEvolveStats).
      // The base card is still underneath at state.field[newIndex].
      const valuesChanged = seedEvolveStats(
        state,
        state.field[newIndex],
        card,
        newIndex,
      );

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Evolved to ${card}`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "evolve",
        updates: [
          { type: "log", data: { text: `Evolved to ${card}`, card } },
          { type: "evoField", data: state.evoField },
          { type: "evoDeck", data: state.evoDeck },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    feedCardOnField: (state, action) => {
      // const card = action.payload.card;
      const newIndex = action.payload.index;
      const carrots = action.payload.carrots;
      const cardIndex = action.payload.indexInEvolveDeck;

      state.evoDeck = state.evoDeck.filter((_, i) => i !== cardIndex);
      if (carrots === 1) {
        const newField = [
          ...state.evoField.slice(0, newIndex),
          "Carrot-1",
          ...state.evoField.slice(newIndex + 1),
        ];
        state.evoField = newField;
      } else {
        const numOfCarrots = Number(state.evoField[newIndex].slice(-1));
        console.log("numOfCarrots", numOfCarrots);
        const newField = [
          ...state.evoField.slice(0, newIndex),
          `Carrot-${numOfCarrots + 1}`,
          ...state.evoField.slice(newIndex + 1),
        ];
        state.evoField = newField;
      }
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Fed ${state.field[newIndex]} 1 Carrot`,
          // card: card,
          card: state.field[newIndex],
        },
      ];

      socket.emit("send msg", {
        type: "feedCardOnField",
        updates: [
          {
            type: "log",
            data: {
              text: `Fed ${state.field[newIndex]} 1 Carrot`,
              card: state.field[newIndex],
            },
          },
          { type: "evoField", data: state.evoField },
          { type: "evoDeck", data: state.evoDeck },
        ],
        room: state.room,
      });
    },
    // Attach an Equipment token to a follower on the top row. The attachment
    // lives in its own per-slot array (equipField) — separate from evoField, so
    // the follower can still evolve while equipped. Card.js renders it as a
    // small badge whose hover shows the equipment as a card preview.
    attachEquipmentOnField: (state, action) => {
      const card = action.payload.card; // equipment token name
      // Slot the equipment sat on; omitted/-1 when dragged straight out of the
      // token deck (nothing on the board to lift off).
      const prevIndex = action.payload.prevIndex;
      const newIndex = action.payload.index; // follower's slot (top row)

      // Lift the equipment token off its own slot…
      if (prevIndex != null && prevIndex >= 0) {
        state.field = [
          ...state.field.slice(0, prevIndex),
          0,
          ...state.field.slice(prevIndex + 1),
        ];
      }
      // …and pin it to the follower.
      state.equipField = [
        ...state.equipField.slice(0, newIndex),
        card,
        ...state.equipField.slice(newIndex + 1),
      ];

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Equipped ${card} to ${state.field[newIndex]}`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "attachEquipmentOnField",
        updates: [
          {
            type: "log",
            data: {
              text: `Equipped ${card} to ${state.field[newIndex]}`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "equipField", data: state.equipField },
        ],
        room: state.room,
      });
    },
    // Carry an attached equipment along when its follower moves slots. No-op
    // when the source slot has none, so callers can dispatch it unconditionally
    // alongside the card move.
    moveEquipmentAtIndex: (state, action) => {
      const prevIndex = action.payload.prevIndex;
      const index = action.payload.index;
      const equip = state.equipField[prevIndex];
      if (!equip || equip === 0) return;
      const newEquipField = [...state.equipField];
      newEquipField[prevIndex] = 0;
      newEquipField[index] = equip;
      state.equipField = newEquipField;
      socket.emit("send msg", {
        type: "moveEquipmentAtIndex",
        updates: [{ type: "equipField", data: state.equipField }],
        room: state.room,
      });
    },
    // Explicit "Remove Equipment" from the card's context menu: clears the
    // attachment like clearEquipmentAtIndex but also logs it (the token is lost).
    removeEquipmentOnField: (state, action) => {
      const index = action.payload;
      const card = state.equipField[index];
      if (!card || card === 0) return;
      state.equipField = [
        ...state.equipField.slice(0, index),
        0,
        ...state.equipField.slice(index + 1),
      ];
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Removed ${card} from ${state.field[index]}`,
          card,
        },
      ];
      socket.emit("send msg", {
        type: "removeEquipmentOnField",
        updates: [
          {
            type: "log",
            data: { text: `Removed ${card} from ${state.field[index]}`, card },
          },
          { type: "equipField", data: state.equipField },
        ],
        room: state.room,
      });
    },
    // Drop an attached equipment (equipment is a token — it simply disappears
    // when its follower leaves the field). No-op when the slot has none.
    clearEquipmentAtIndex: (state, action) => {
      const index = action.payload;
      const equip = state.equipField[index];
      if (!equip || equip === 0) return;
      state.equipField = [
        ...state.equipField.slice(0, index),
        0,
        ...state.equipField.slice(index + 1),
      ];
      socket.emit("send msg", {
        type: "clearEquipmentAtIndex",
        updates: [{ type: "equipField", data: state.equipField }],
        room: state.room,
      });
    },
    setEnemyEquipField: (state, action) => {
      state.enemyEquipField = action.payload;
    },
    rideCardOnField: (state, action) => {
      const newIndex = action.payload.index;
      const cardIndex = action.payload.indexInEvolveDeck;

      state.evoDeck = state.evoDeck.filter((_, i) => i !== cardIndex);

      const newField = [
        ...state.evoField.slice(0, newIndex),
        "Drive Point",
        ...state.evoField.slice(newIndex + 1),
      ];
      state.evoField = newField;

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Ride ${state.field[newIndex]}`,
          card: state.field[newIndex],
        },
      ];

      socket.emit("send msg", {
        type: "rideCardOnField",
        updates: [
          {
            type: "log",
            data: {
              text: `Ride ${state.field[newIndex]}`,
              card: state.field[newIndex],
            },
          },
          { type: "evoField", data: state.evoField },
          { type: "evoDeck", data: state.evoDeck },
        ],
        room: state.room,
      });
    },
    advancedToField: (state, action) => {
      const card = action.payload.card;
      const newIndex = action.payload.index;
      const cardIndex = action.payload.indexInEvolveDeck;
      state.evoDeck = state.evoDeck.filter((_, i) => i !== cardIndex);
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const newField = [
        ...state.field.slice(0, newIndex),
        card,
        ...state.field.slice(newIndex + 1),
      ];
      state.field = newField;

      // Advanced followers from the evolve deck land on the top row like a play,
      // so seed their Atk/Def icons from the card's stats too (see
      // seedFollowerStats).
      const valuesChanged = seedFollowerStats(state, card, newIndex);

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Added ${card} to ${rowName(newIndex)} from evolve deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "advancedToField",
        updates: [
          {
            type: "log",
            data: {
              text: `Added ${card} to ${rowName(newIndex)} from evolve deck`,
              card,
            },
          },
          { type: "field", data: state.field },
          { type: "evoDeck", data: state.evoDeck },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    backToEvolveDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.evoField.slice(0, cardIndex),
        0,
        ...state.evoField.slice(cardIndex + 1),
      ];
      state.evoField = newField;

      if (card.slice(0, 6) === "Carrot") {
        const numOfCarrots = Number(card.slice(-1));
        for (let i = 0; i < numOfCarrots; i++)
          state.evoDeck = [...state.evoDeck, { card: "Carrot", status: true }];
      } else {
        state.evoDeck = [...state.evoDeck, { card: card, status: true }];
      }

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Returned ${card} to evolve deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "backToEvolveDeck",
        updates: [
          {
            type: "log",
            data: { text: `Returned ${card} to evolve deck`, card },
          },
          { type: "evoField", data: state.evoField },
          { type: "evoDeck", data: state.evoDeck },
        ],
        room: state.room,
      });
    },
    advancedBackToEvolveDeck: (state, action) => {
      const card = action.payload.card;
      const cardIndex = action.payload.index;
      const newField = [
        ...state.field.slice(0, cardIndex),
        0,
        ...state.field.slice(cardIndex + 1),
      ];
      state.field = newField;

      state.evoDeck = [...state.evoDeck, { card: card, status: true }];

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Returned ${card} to evolve deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "advancedBackToEvolveDeck",
        updates: [
          {
            type: "log",
            data: { text: `Returned ${card} to evolve deck`, card },
          },
          { type: "field", data: state.field },
          { type: "evoDeck", data: state.evoDeck },
        ],
        room: state.room,
      });
    },
    flipEvoCard: (state, action) => {
      const card = action.payload.name;
      const idx = action.payload.idx;
      const status = action.payload.status;

      state.evoDeck = state.evoDeck.filter((_, i) => i !== idx);

      state.evoDeck = [
        ...state.evoDeck.slice(0, idx),
        {
          card: card,
          status: !status,
        },
        ...state.evoDeck.slice(idx),
      ];

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Flipped ${card} in evolve deck`,
          card,
        },
      ];

      socket.emit("send msg", {
        type: "flipEvoCard",
        updates: [
          {
            type: "log",
            data: { text: `Flipped ${card} in evolve deck`, card },
          },
          { type: "evoDeck", data: state.evoDeck },
        ],
        room: state.room,
      });
    },
    switchEvoCard: (state, action) => {
      const card = action.payload.name;
      const idx = action.payload.idx;
      const newCard = doubleEvoOtherSide(card);
      if (!newCard) return;

      state.evoDeck[idx].card = newCard;

      // Keep the opponent's copy of our evolve deck in step (it's public info,
      // same as flipEvoCard above).
      socket.emit("send msg", {
        type: "switchEvoCard",
        updates: [{ type: "evoDeck", data: state.evoDeck }],
        room: state.room,
      });
    },
    // Switch an already-evolved dual-sided card on the field to its other side
    // (e.g. Orchis, Resolute Puppet <-> Orchis, Vengeful Puppet). It's the same
    // physical card, so counters/engage/equipment stay put; the Atk/Def overlay
    // shifts by the printed delta between sides (Vania 4/4 -> Blood Queen 6/6),
    // carrying any current buff/reduction — same rule as evolving.
    switchEvoCardOnField: (state, action) => {
      const index = action.payload.index;
      const card = state.evoField[index];
      const newCard = doubleEvoOtherSide(card);
      if (!newCard) return;

      state.evoField = [
        ...state.evoField.slice(0, index),
        newCard,
        ...state.evoField.slice(index + 1),
      ];

      const valuesChanged = seedEvolveStats(state, card, newCard, index);

      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Switched ${card} to ${newCard}`,
          card: newCard,
        },
      ];

      socket.emit("send msg", {
        type: "switchEvoCardOnField",
        updates: [
          {
            type: "log",
            data: { text: `Switched ${card} to ${newCard}`, card: newCard },
          },
          { type: "evoField", data: state.evoField },
          ...(valuesChanged
            ? [{ type: "values", data: state.customValues }]
            : []),
        ],
        room: state.room,
      });
    },
    createLessonTokens: (state) => {
      let cardsInEX = false;

      for (let i = 6; i < 10; i++) {
        if (state.field[i] !== 0) {
          cardsInEX = true;
          break;
        }
      }
      if (!cardsInEX) {
        let card;
        let chance = Math.random();
        if (chance < 0.2 && chance > 0.1) card = "Enchanted Slippers TOKEN";
        else if (chance <= 0.1) card = "Enchanted Dress TOKEN";
        else card = "Cool Earrings TOKEN";

        const newField = [
          ...state.field.slice(0, 5),
          card,
          card,
          card,
          card,
          card,
        ];

        state.field = newField;
        const date = new Date().toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
        });
        for (let i = 0; i < 5; i++) {
          state.gameLog = [
            ...state.gameLog,
            {
              text: `[${date}] (Me): Added ${card} to EX area`,
              card,
            },
          ];
        }

        socket.emit("send msg", {
          type: "createLessonTokens",
          updates: [
            {
              type: "log",
              data: { text: `Added 5 ${card} to EX area`, card },
            },
            { type: "field", data: state.field },
          ],
          room: state.room,
        });
      }
    },
    syncFromEngine: (state, action) => {
      const s = action.payload;
      if (s.hand !== undefined) state.hand = s.hand;
      if (s.handInstanceIds !== undefined) state.handInstanceIds = s.handInstanceIds;
      if (s.fieldInstanceIds !== undefined) state.fieldInstanceIds = s.fieldInstanceIds;
      if (s.enemyFieldInstanceIds !== undefined) {
        state.enemyFieldInstanceIds = s.enemyFieldInstanceIds;
      }
      if (s.field !== undefined) state.field = s.field;
      if (s.evoField !== undefined) state.evoField = s.evoField;
      if (s.engagedField !== undefined) state.engagedField = s.engagedField;
      if (s.customValues !== undefined) state.customValues = s.customValues;
      if (s.wardField !== undefined) state.wardField = s.wardField;
      if (s.baneField !== undefined) state.baneField = s.baneField;
      if (s.auraField !== undefined) state.auraField = s.auraField;
      if (s.exPlayCostField !== undefined) state.exPlayCostField = s.exPlayCostField;
      if (s.enemyExPlayCostField !== undefined) {
        state.enemyExPlayCostField = s.enemyExPlayCostField;
      }
      if (s.cemetery !== undefined) state.cemetery = s.cemetery;
      if (s.cemeteryInstanceIds !== undefined) state.cemeteryInstanceIds = s.cemeteryInstanceIds;
      if (s.played !== undefined)
        state.played = Array.isArray(s.played) ? s.played.slice(0, 1) : [];
      if (s.evoDeck !== undefined) state.evoDeck = s.evoDeck;
      if (s.deck !== undefined) state.deck = s.deck;
      if (s.playPoints !== undefined) state.playPoints = s.playPoints;
      if (s.evoPoints !== undefined) state.evoPoints = s.evoPoints;
      if (s.playerHealth !== undefined) state.playerHealth = s.playerHealth;
      if (s.leaderActive !== undefined) state.leaderActive = s.leaderActive;
      if (s.superEvoActive !== undefined) state.superEvoActive = s.superEvoActive;
      if (s.enemySuperEvoActive !== undefined) {
        state.enemySuperEvoActive = s.enemySuperEvoActive;
      }
      if (s.enemyHand !== undefined) state.enemyHand = s.enemyHand;
      if (s.enemyField !== undefined) state.enemyField = s.enemyField;
      if (s.enemyEvoField !== undefined) state.enemyEvoField = s.enemyEvoField;
      if (s.enemyEngagedField !== undefined) state.enemyEngagedField = s.enemyEngagedField;
      if (s.enemyCustomValues !== undefined) state.enemyCustomValues = s.enemyCustomValues;
      if (s.enemyCemetery !== undefined) state.enemyCemetery = s.enemyCemetery;
      if (s.enemyPlayPoints !== undefined) state.enemyPlayPoints = s.enemyPlayPoints;
      if (s.enemyEvoPoints !== undefined) state.enemyEvoPoints = s.enemyEvoPoints;
      if (s.enemyHealth !== undefined) state.enemyHealth = s.enemyHealth;
      if (s.enemyLeaderActive !== undefined) state.enemyLeaderActive = s.enemyLeaderActive;
    },
    setMyArt: (state, action) => {
      state.myArt = action.payload || {};
    },
    setEnemyArt: (state, action) => {
      state.enemyArt = action.payload || {};
    },
    setFavoriteTokens: (state, action) => {
      state.favoriteTokens = Array.isArray(action.payload) ? action.payload : [];
    },
    restoreOwnState: (state, action) => {
      const s = action.payload;
      if (s.myArt !== undefined) state.myArt = s.myArt;
      if (s.enemyArt !== undefined) state.enemyArt = s.enemyArt;
      if (s.favoriteTokens !== undefined) state.favoriteTokens = s.favoriteTokens;
      if (s.field !== undefined) state.field = s.field;
      if (s.evoField !== undefined) state.evoField = s.evoField;
      if (s.equipField !== undefined) state.equipField = s.equipField;
      if (s.hand !== undefined) state.hand = s.hand;
      if (s.playerHealth !== undefined) state.playerHealth = s.playerHealth;
      if (s.playPoints !== undefined) state.playPoints = s.playPoints;
      if (s.evoPoints !== undefined) state.evoPoints = s.evoPoints;
      if (s.turn !== undefined) state.turn = s.turn;
      if (s.enemyTurn !== undefined) state.enemyTurn = s.enemyTurn;
      if (s.leader !== undefined) state.leader = s.leader;
      if (s.leaderActive !== undefined) state.leaderActive = s.leaderActive;
      if (s.superEvoActive !== undefined)
        state.superEvoActive = s.superEvoActive;
      if (s.cardback !== undefined) state.cardback = s.cardback;
      if (s.cemetery !== undefined) state.cemetery = s.cemetery;
      if (s.banish !== undefined) state.banish = s.banish;
      if (s.played !== undefined)
        state.played = Array.isArray(s.played) ? s.played.slice(0, 1) : [];
      if (s.evoDeck !== undefined) state.evoDeck = s.evoDeck;
      if (s.deck !== undefined) state.deck = s.deck;
      if (s.engagedField !== undefined) state.engagedField = s.engagedField;
      if (s.counterField !== undefined) state.counterField = s.counterField;
      if (s.auraField !== undefined) state.auraField = s.auraField;
      if (s.baneField !== undefined) state.baneField = s.baneField;
      if (s.wardField !== undefined) state.wardField = s.wardField;
      if (s.keywordField !== undefined) state.keywordField = s.keywordField;
      if (s.enemyKeywordField !== undefined)
        state.enemyKeywordField = s.enemyKeywordField;
      if (s.customValues !== undefined) state.customValues = s.customValues;
      if (s.customStatus !== undefined) state.customStatus = s.customStatus;
      // Chat and game logs survive a reload with the rest of the saved state,
      // along with my announced identity.
      if (s.chatLog !== undefined) state.chatLog = s.chatLog;
      if (s.gameLog !== undefined) state.gameLog = s.gameLog;
      if (s.myName !== undefined) state.myName = s.myName;
      if (s.myAvatar !== undefined) state.myAvatar = s.myAvatar;
      state.cardSelectedInHand = -1;
      state.enemyCardSelectedInHand = -1;
      state.cardSelectedOnField = -1;
      state.enemyCardSelectedOnField = -1;
    },
    setField: (state, action) => {
      state.field = action.payload;
    },
    setEnemyField: (state, action) => {
      state.enemyField = action.payload;
    },
    setEnemyEvoField: (state, action) => {
      state.enemyEvoField = action.payload;
    },
    setEnemyEngaged: (state, action) => {
      state.enemyEngagedField = action.payload;
    },
    setEnemyCemetery: (state, action) => {
      state.enemyCemetery = action.payload;
    },
    setEnemyBanish: (state, action) => {
      state.enemyBanish = action.payload;
    },
    setEnemyEvoDeck: (state, action) => {
      state.enemyEvoDeck = action.payload;
    },
    setEnemyCustomValues: (state, action) => {
      state.enemyCustomValues = action.payload;
    },
    setEnemyLeader: (state, action) => {
      state.enemyLeader = action.payload;
    },
    setEnemyCounter: (state, action) => {
      state.enemyCounterField = action.payload;
    },
    setEnemyAura: (state, action) => {
      state.enemyAuraField = action.payload;
    },
    setEnemyBane: (state, action) => {
      state.enemyBaneField = action.payload;
    },
    setEnemyWard: (state, action) => {
      state.enemyWardField = action.payload;
    },
    setEnemyLeaderActive: (state, action) => {
      state.enemyLeaderActive = action.payload;
    },
    setLeaderActive: (state, action) => {
      state.leaderActive = action.payload;
    },
    setEnemySuperEvoActive: (state, action) => {
      state.enemySuperEvoActive = action.payload;
    },
    setSuperEvoActive: (state, action) => {
      state.superEvoActive = action.payload;
    },
    exitGame: (state) => {
      state.room = "";
      state.enemyOnlineStatus = true;
      state.enemyLeftGame = false;
      state.gameLog = [];
      state.matchStartEpoch = 0;
      state.chatLog = [];
      state.lastChatMessage = "";
      state.enemyName = "";
      state.enemyAvatar = "";
      state.enemyIdentityReceived = false;
      state.deck = [];
      state.evoDeck = [];
      state.hand = [];
      state.enemyHand = [];
      state.cardSelectedInHand = -1;
      state.enemyCardSelectedInHand = -1;
      state.cardSelectedOnField = -1;
      state.enemyCardSelectedOnField = -1;
      state.showDice = false;
      state.enemyDice = { show: false, roll: 1 };
      state.showEnemyHand = false;
      state.showHand = false;
      state.showEnemyCard = false;
      state.enemyCard = "";
      state.enemyRevealedCards = [];
      state.enemyRevealedCardsOpen = false;
      state.viewingOpponentTopCards = false;
      state.enemyDeckSize = 0;
      state.enemyLeader = "";
      state.leaderActive = false;
      state.enemyLeaderActive = false;
      state.superEvoActive = false;
      state.enemySuperEvoActive = false;
      state.cardback = "";
      state.enemyCardback = "";
      state.leader = "";
      state.evoPoints = 0;
      state.enemyEvoPoints = 0;
      state.turn = 0;
      state.enemyTurn = 0;
      state.playPoints = { available: 0, max: 0 };
      state.enemyPlayPoints = { available: 0, max: 0 };
      state.playerHealth = 20;
      state.enemyHealth = 20;
      state.field = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.evoField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyEvoField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.equipField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyEquipField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.counterField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyCounterField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.currentCard = "";
      state.currentEvo = "";
      state.rematchStatus = false;
      state.enemyRematchStatus = false;
      state.banish = [];
      state.enemyBanish = [];
      state.cemetery = [];
      state.enemyCemetery = [];
      state.played = [];
      state.enemyArrow = { idx: -1, show: false };
      state.enemyEvoDeck = [
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
      ];
      state.engagedField = [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ];
      state.enemyEngagedField = [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ];
      state.customValues = [
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
      ];
      state.enemyCustomValues = [
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
      ];
    },
    reset: (state) => {
      state.enemyLeftGame = false;
      state.gameLog = [];
      state.matchStartEpoch = Date.now();
      state.deck = state.initialDeck;
      state.deck = state.deck.toSorted(() => Math.random() - 0.5);
      state.evoDeck = state.initialEvoDeck;
      state.hand = [];
      state.enemyHand = [];
      state.showDice = false;
      state.enemyDice = { show: false, roll: 1 };
      state.showEnemyHand = false;
      state.showHand = false;
      state.showEnemyCard = false;
      state.enemyCard = "";
      state.enemyRevealedCards = [];
      state.enemyRevealedCardsOpen = false;
      state.viewingOpponentTopCards = false;
      state.cardSelectedInHand = -1;
      state.enemyCardSelectedInHand = -1;
      state.cardSelectedOnField = -1;
      state.enemyCardSelectedOnField = -1;
      state.enemyDeckSize = 0;
      state.leaderActive = false;
      state.enemyLeaderActive = false;
      state.superEvoActive = false;
      state.enemySuperEvoActive = false;
      state.evoPoints = 0;
      state.enemyEvoPoints = 0;
      state.turn = 0;
      state.enemyTurn = 0;
      state.playPoints = { available: 0, max: 0 };
      state.enemyPlayPoints = { available: 0, max: 0 };
      state.playerHealth = 20;
      state.enemyHealth = 20;
      state.field = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.evoField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyEvoField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.equipField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyEquipField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.counterField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyCounterField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.currentCard = "";
      state.currentEvo = "";
      state.rematchStatus = false;
      state.enemyRematchStatus = false;
      state.banish = [];
      state.enemyBanish = [];
      state.cemetery = [];
      state.enemyCemetery = [];
      state.played = [];
      state.enemyArrow = { idx: -1, show: false };
      state.enemyEvoDeck = [
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
        { card: "", status: false },
      ];
      state.engagedField = [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ];
      state.enemyEngagedField = [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ];
      state.auraField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyAuraField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.baneField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyBaneField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.wardField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.enemyWardField = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      state.keywordField = [[], [], [], [], [], [], [], [], [], []];
      state.enemyKeywordField = [[], [], [], [], [], [], [], [], [], []];
      state.customStatus = [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ];
      state.customValues = [
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
      ];
      state.enemyCustomValues = [
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
        { showAtk: false, atk: 0, showDef: false, def: 0 },
      ];
    },
    // Swap in a different saved deck WITHOUT leaving the room. Unlike `reset`
    // (a symmetric rematch that wipes both boards), this is one-sided: it loads
    // a new deck as my draw pile + evolve deck and re-seeds only MY side to a
    // fresh start, then pushes that cleared state to the opponent so their view
    // of me matches. The opponent's own board/hand/health are left untouched —
    // a mutual reset is what the Rematch handshake is for.
    // payload: { deck: [names], evoDeck: [names], art: {name->cardNo}, deckClass, favoriteTokens?: [names] }
    swapDeck: (state, action) => {
      const { deck, evoDeck, art, deckClass, favoriteTokens } = action.payload;
      const date = new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Load the new deck as both the live pile and the reset seed (mirrors
      // setDeck/setEvoDeck). `deck` is a flat name array; `evoDeck` becomes the
      // {card,status} shape the evolve-deck UI expects.
      const shuffled = [...deck].sort(() => Math.random() - 0.5);
      state.deck = shuffled;
      state.initialDeck = shuffled;
      const evo = (evoDeck || []).map((card) => ({ card, status: false }));
      state.evoDeck = evo;
      state.initialEvoDeck = evo;
      state.myArt = art || {};
      state.favoriteTokens = Array.isArray(favoriteTokens) ? favoriteTokens : [];
      if (deckClass) state.deckClass = deckClass;

      // Clear MY side to a fresh start.
      const zeros = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      const falses = [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ];
      const emptyValues = zeros.map(() => ({
        showAtk: false,
        atk: 0,
        showDef: false,
        def: 0,
      }));
      state.hand = [];
      state.handInstanceIds = [];
      state.field = [...zeros];
      state.evoField = [...zeros];
      state.equipField = [...zeros];
      state.counterField = [...zeros];
      state.auraField = [...zeros];
      state.baneField = [...zeros];
      state.wardField = [...zeros];
      state.keywordField = zeros.map(() => []);
      state.engagedField = [...falses];
      state.customStatus = [...falses];
      state.customValues = emptyValues;
      state.exPlayCostField = zeros.map(() => null);
      state.fieldInstanceIds = zeros.map(() => null);
      state.cemetery = [];
      state.cemeteryInstanceIds = [];
      state.banish = [];
      state.played = [];
      state.currentCard = "";
      state.currentEvo = "";
      state.currentCardIndex = -1;
      state.superEvoActive = false;
      state.leaderActive = false;
      state.evoPoints = 0;
      state.turn = 0;
      state.playPoints = { available: 0, max: 0 };
      state.playerHealth = 20;

      state.gameLog = [
        ...state.gameLog,
        {
          text: `[${date}] (Me): Swapped in a new deck`,
          card: "",
        },
      ];

      socket.emit("send msg", {
        type: "swapDeck",
        updates: [
          { type: "log", data: { text: "Opponent swapped in a new deck", card: "" } },
          { type: "field", data: state.field },
          { type: "evoField", data: state.evoField },
          { type: "equipField", data: state.equipField },
          { type: "counter", data: state.counterField },
          { type: "aura", data: state.auraField },
          { type: "bane", data: state.baneField },
          { type: "ward", data: state.wardField },
          { type: "keyword", data: state.keywordField },
          { type: "engaged", data: state.engagedField },
          { type: "values", data: state.customValues },
          { type: "hand", data: state.hand },
          { type: "deckSize", data: state.deck.length },
          { type: "cemetery", data: state.cemetery },
          { type: "banish", data: state.banish },
          { type: "evoDeck", data: state.evoDeck },
          { type: "art", data: state.myArt },
          { type: "health", data: state.playerHealth },
          { type: "evoPoints", data: state.evoPoints },
          { type: "playPoints", data: state.playPoints },
          { type: "leaderActive", data: state.leaderActive },
          { type: "superEvoActive", data: state.superEvoActive },
        ],
        room: state.room,
      });
    },
  },
});

export const {
  drawFromDeck,
  drawFourFromDeck,
  placeToFieldFromHand,
  placeToFieldFromDeck,
  placeToExFromTopOfDeck,
  placeToFieldFromCemetery,
  placeToFieldFromBanish,
  addToHandFromDeck,
  addToHandFromDeckWithoutRevealing,
  addToHandFromField,
  addToHandFromCemetery,
  addToBanishFromCemetery,
  addToHandFromBanish,
  addToCemeteryFromBanish,
  placeToTopOfDeckFromBanish,
  placeToBotOfDeckFromBanish,
  placeToTopOfDeckFromHand,
  placeToBotOfDeckFromHand,
  placeToTopOfDeckFromCemetery,
  placeToBotOfDeckFromCemetery,
  placeToTopOfDeckFromField,
  placeToBotOfDeckFromField,
  placeToCemeteryFromField,
  placeToCemeteryFromHand,
  placeToBanishFromHand,
  placeTokenOnField,
  placeToBanishFromField,
  setPlayed,
  playCardToSharedZone,
  movePlayedToHand,
  movePlayedToField,
  movePlayedToTopOfDeck,
  movePlayedToBotOfDeck,
  movePlayedToCemetery,
  movePlayedToBanish,
  addToTopOfDeckFromDeck,
  addToBotOfDeckFromDeck,
  addToCemeteryFromDeck,
  addToCemeteryFromTopOfDeck,
  addToBanishFromDeck,
  addToBanishFromTopOfDeck,
  removeTokenOnField,
  moveCardOnField,
  moveEvoAndBaseOnField,
  transferToOpponentField,
  mulliganFour,
  shuffleDeck,
  evolveCardOnField,
  feedCardOnField,
  rideCardOnField,
  attachEquipmentOnField,
  moveEquipmentAtIndex,
  clearEquipmentAtIndex,
  removeEquipmentOnField,
  setEnemyEquipField,
  advancedToField,
  backToEvolveDeck,
  advancedBackToEvolveDeck,
  flipEvoCard,
  switchEvoCard,
  switchEvoCardOnField,
  clearEngagedAtIndex,
  moveEngagedAtIndex,
  clearCountersAtIndex,
  moveCountersAtIndex,
  clearStatusAtIndex,
  moveStatusAtIndex,
  showAtk,
  showDef,
  modifyAtk,
  hideAtk,
  hideDef,
  modifyDef,
  clearValuesAtIndex,
  moveValuesAtIndex,
  modifyCounter,
  addAura,
  addBane,
  addWard,
  showStatus,
  hideStatus,
  duplicateCardOnField,
  createLessonTokens,
  shuffleCards,
  setMyArt,
  setEnemyArt,
  setFavoriteTokens,
  toggleKeyword,
  setEnemyKeyword,
  restoreOwnState,
  syncFromEngine,
  reset,
  swapDeck,
  exitGame,
  setSuperEvoActive,
  setEnemySuperEvoActive,
  setRematchStatus,
  setEnemyRematchStatus,
  setMatchStart,
  setCurrentCard,
  setCurrentCardIndex,
  setCurrentEvo,
  setRoom,
  setActiveUsers,
  setLeaderActive,
  setDeck,
  setEvoDeck,
  setField,
  setEnemyField,
  setEnemyEvoField,
  setEnemyEngaged,
  setEngaged,
  setEnemyCemetery,
  setEnemyBanish,
  setEnemyEvoDeck,
  setEnemyCustomValues,
  setEnemyHand,
  setEnemyDeckSize,
  setEnemyEvoPoints,
  setEnemyPlayPoints,
  setEnemyHealth,
  setEnemyLog,
  setChat,
  setEnemyOnlineStatus,
  setSelfOnlineStatus,
  setSelfConnectionState,
  setSelfResyncing,
  setLastChatMessage,
  setEnemyChat,
  setMyIdentity,
  setEnemyIdentity,
  setViewingCardsLog,
  setViewingDeckLog,
  setEvoPoints,
  setTurn,
  setEnemyTurn,
  logHealthDiff,
  setPlayPoints,
  setHealth,
  setLeader,
  setLeaderSilent,
  setDeckClass,
  setCardBack,
  setEnemyCardBack,
  setEnemyLeader,
  setShowEnemyHand,
  setShowHand,
  setViewingDeck,
  setViewingTopCards,
  setViewingCemetery,
  setViewingEvoDeck,
  setViewingCemeteryOpponent,
  setViewingEvoDeckOpponent,
  setEnemyViewingDeck,
  setEnemyViewingHand,
  setEnemyViewingCemetery,
  setEnemyViewingEvoDeck,
  setEnemyViewingCemeteryOpponent,
  setEnemyViewingEvoDeckOpponent,
  setEnemyViewingTopCards,
  setEnemyLeftGame,
  setEnemyCounter,
  setEnemyAura,
  setEnemyBane,
  setEnemyWard,
  setCardSelectedInHand,
  setEnemyCardSelectedInHand,
  setCardSelectedOnField,
  setEnemyCardSelectedOnField,
  setShowEnemyCard,
  setEnemyCard,
  setEnemyRevealedCards,
  setEnemyRevealedCardsOpen,
  setViewingOpponentTopCards,
  setDice,
  setShowDice,
  setEnemyDice,
  setEnemyLeaderActive,
} = CardSlice.actions;
